import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Calculator, Filter, CheckCircle, XCircle, ArrowLeft, AlertTriangle, Clock, HelpCircle, Eye } from 'lucide-react';
import MathText from '@/components/MathText';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const TAG_OPTIONS = [
  { value: 'careless_error', label: 'Careless Error', color: '#f59e0b', icon: AlertTriangle },
  { value: 'didnt_know_concept', label: "Didn't Know Concept", color: '#ef4444', icon: XCircle },
  { value: 'ran_out_of_time', label: 'Ran Out of Time', color: '#6366f1', icon: Clock },
  { value: 'misread_question', label: 'Misread Question', color: '#8b5cf6', icon: HelpCircle },
];

const TAG_MAP = Object.fromEntries(TAG_OPTIONS.map(t => [t.value, t]));

export default function MistakeJournal({ user }) {
  const navigate = useNavigate();
  const [mistakes, setMistakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [filterTag, setFilterTag] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterResolved, setFilterResolved] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    document.title = 'Mistake Journal | Practice4SAT';
    collectAndFetch();
  }, []);

  useEffect(() => { fetchMistakes(); }, [filterTag, filterSection, filterResolved]);

  const collectAndFetch = async () => {
    try {
      await axios.post(`${API}/mistakes/collect`);
    } catch (e) { /* ignore */ }
    fetchMistakes();
  };

  const fetchMistakes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTag) params.append('tag', filterTag);
      if (filterSection) params.append('section', filterSection);
      if (filterResolved) params.append('resolved', 'true');
      else params.append('resolved', 'false');

      const res = await axios.get(`${API}/mistakes?${params}`);
      setMistakes(res.data.mistakes || []);
      setSummary(res.data);
    } catch (e) {
      toast.error('Failed to load mistakes');
    }
    setLoading(false);
  };

  const handleTag = async (questionId, sessionId, tag) => {
    try {
      await axios.put(`${API}/mistakes/${questionId}/${sessionId}/tag`, {
        question_id: questionId, session_id: sessionId, tag
      });
      setMistakes(prev => prev.map(m =>
        m.question_id === questionId && m.session_id === sessionId ? { ...m, tag } : m
      ));
      toast.success('Tagged!');
    } catch (e) {
      toast.error('Failed to tag');
    }
  };

  const handleResolve = async (questionId, sessionId) => {
    try {
      await axios.put(`${API}/mistakes/${questionId}/${sessionId}/resolve`, {
        resolved: true
      });
      setMistakes(prev => prev.filter(m => !(m.question_id === questionId && m.session_id === sessionId)));
      toast.success('Marked as resolved!');
    } catch (e) {
      toast.error('Failed to resolve');
    }
  };

  const toggleExpand = (qid, sid) => {
    const key = `${qid}-${sid}`;
    setExpandedId(prev => prev === key ? null : key);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header variant="authenticated" user={user} logoClickPath="/dashboard" />
      <main style={{ flex: 1, background: '#f5f5f5', padding: '2rem' }}>
        <div className="dashboard-content">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn" style={{ padding: '0.5rem' }}>
                <ArrowLeft size={20} />
              </Button>
              <h1 data-testid="page-title" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a202c', margin: 0 }}>Mistake Journal</h1>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
              <span data-testid="total-count">{summary.total || 0} total</span>
              <span>|</span>
              <span data-testid="unresolved-count" style={{ color: '#ef4444', fontWeight: 600 }}>{summary.unresolved || 0} unresolved</span>
            </div>
          </div>

          {/* Filters */}
          <div data-testid="filters" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <select
              data-testid="filter-tag"
              value={filterTag}
              onChange={e => setFilterTag(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', background: '#fff' }}
            >
              <option value="">All Tags</option>
              {TAG_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              <option value="untagged">Untagged</option>
            </select>
            <select
              data-testid="filter-section"
              value={filterSection}
              onChange={e => setFilterSection(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', background: '#fff' }}
            >
              <option value="">All Sections</option>
              <option value="rw">Reading & Writing</option>
              <option value="math">Math</option>
            </select>
            <Button
              variant={filterResolved ? 'default' : 'outline'}
              data-testid="filter-resolved"
              onClick={() => setFilterResolved(!filterResolved)}
              style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}
            >
              {filterResolved ? 'Showing Resolved' : 'Show Resolved'}
            </Button>
          </div>

          {/* Mistake Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          ) : mistakes.length === 0 ? (
            <div data-testid="empty-state" style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <CheckCircle size={48} style={{ color: '#22c55e', margin: '0 auto 1rem' }} />
              <h3 style={{ fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                {filterResolved ? 'No resolved mistakes yet' : 'No unresolved mistakes!'}
              </h3>
              <p style={{ color: '#6b7280' }}>
                {filterResolved ? 'Resolve some mistakes to see them here.' : 'Great job! Keep practicing to maintain your skills.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {mistakes.map((m) => {
                const key = `${m.question_id}-${m.session_id}`;
                const isExpanded = expandedId === key;
                const tagInfo = TAG_MAP[m.tag];
                const SectionIcon = m.section === 'rw' ? BookOpen : Calculator;

                return (
                  <div key={key} data-testid={`mistake-card-${m.question_id}`} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    {/* Card Header */}
                    <div
                      style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: '1rem', flexWrap: 'wrap' }}
                      onClick={() => toggleExpand(m.question_id, m.session_id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                        <SectionIcon size={18} style={{ color: m.section === 'rw' ? '#6366f1' : '#0030b3', flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.domain || 'Unknown Topic'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            {m.source === 'test' ? `Test: ${m.test_id}` : 'Practice'} | {m.difficulty || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                        {tagInfo ? (
                          <Badge style={{ background: `${tagInfo.color}20`, color: tagInfo.color, fontSize: '0.75rem' }}>
                            {tagInfo.label}
                          </Badge>
                        ) : (
                          <Badge style={{ background: '#f3f4f6', color: '#9ca3af', fontSize: '0.75rem' }}>Untagged</Badge>
                        )}
                        <Eye size={16} style={{ color: '#9ca3af' }} />
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
                        {/* Question Text */}
                        <div style={{ padding: '1rem 0', fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
                          <MathText>{m.question_text}</MathText>
                        </div>

                        {/* Answers */}
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                          <div style={{ padding: '0.5rem 0.75rem', borderRadius: 8, background: '#fee2e2', fontSize: '0.9rem' }}>
                            <strong>Your answer:</strong> <span style={{ color: '#ef4444' }}>{m.user_answer || '(blank)'}</span>
                          </div>
                          <div style={{ padding: '0.5rem 0.75rem', borderRadius: 8, background: '#dcfce7', fontSize: '0.9rem' }}>
                            <strong>Correct:</strong> <span style={{ color: '#16a34a' }}>{m.correct_answer}</span>
                          </div>
                        </div>

                        {/* Explanation */}
                        {m.explanation && (
                          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', lineHeight: 1.6, color: '#475569' }}>
                            <strong>Explanation:</strong>
                            <div style={{ marginTop: '0.25rem' }}><MathText>{m.explanation}</MathText></div>
                          </div>
                        )}

                        {/* Tag Buttons */}
                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>Why did you get it wrong?</div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {TAG_OPTIONS.map(t => (
                              <Button
                                key={t.value}
                                variant={m.tag === t.value ? 'default' : 'outline'}
                                data-testid={`tag-btn-${t.value}`}
                                onClick={() => handleTag(m.question_id, m.session_id, t.value)}
                                style={{
                                  fontSize: '0.8rem', padding: '0.3rem 0.65rem',
                                  background: m.tag === t.value ? t.color : undefined,
                                  borderColor: t.color,
                                  color: m.tag === t.value ? '#fff' : t.color
                                }}
                              >
                                {t.label}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Resolve */}
                        <Button
                          data-testid={`resolve-btn-${m.question_id}`}
                          onClick={() => handleResolve(m.question_id, m.session_id)}
                          style={{ background: '#22c55e', color: '#fff', fontSize: '0.85rem' }}
                        >
                          <CheckCircle size={16} style={{ marginRight: '0.4rem' }} />
                          Mark as Resolved
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
